(() => {
  if (customElements.get('tripwire556-shirt-offer')) return;

  customElements.define('tripwire556-shirt-offer', class Tripwire556ShirtOffer extends HTMLElement {
    constructor() {
      super();
      this.nativeFetch = window.__nativeFetch || window.fetch.bind(window);
      this.state = {
        design: this.dataset.defaultDesign || 'Triggernometry',
        size: this.dataset.defaultSize || 'Large',
        busy: false,
      };
      this.variants = [];
      this.variantMap = new Map();
      this.redirectUrl = this.normalizeRedirectUrl(this.dataset.redirectUrl);
      this.insuranceVariantId = Number(this.dataset.insuranceVariantId || 0);
      // Older saved section settings can still reference the retired variant.
      if (this.insuranceVariantId === 50757737873703) {
        const standardVariantId = Number(window.theme?.shopSettings?.shippingInsuranceVariantId);
        this.insuranceVariantId = standardVariantId && standardVariantId !== 50757737873703
          ? standardVariantId
          : 51706610188583;
      }
      this.insuranceVariantIds = new Set([
        this.insuranceVariantId,
        ...(Array.isArray(window.theme?.shopSettings?.shippingInsuranceVariantIds)
          ? window.theme.shopSettings.shippingInsuranceVariantIds
          : [51706610188583, 51706610221351, 51706610254119]),
        ...(Array.isArray(window.theme?.shopSettings?.shippingInsuranceLegacyVariantIds)
          ? window.theme.shopSettings.shippingInsuranceLegacyVariantIds
          : [50757737873703]),
      ].map(Number).filter(Boolean));
      this.primaryCta = this.dataset.primaryCta || 'Add Range Pack - [price]';
    }

    connectedCallback() {
      this.variants = this.parseJson('[data-tripwire556-variants]', []);
      this.variants.forEach((variant) => {
        this.variantMap.set(this.getVariantKey(variant.design, variant.size), variant);
      });

      this.designButtons = Array.from(this.querySelectorAll('[data-tripwire556-design]'));
      this.sizeInputs = Array.from(this.querySelectorAll('[data-tripwire556-size]'));
      this.addButtons = Array.from(this.querySelectorAll('[data-tripwire556-add], [data-tripwire556-sticky-add]'));
      this.sticky = this.querySelector('[data-tripwire556-sticky]');
      this.actions = this.querySelector('.tripwire556-actions');

      this.designButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.state.design = button.dataset.design || this.state.design;
          this.showError('');
          this.updateView();
          this.track('tripwire556_design_selected', { shirt_design: this.state.design });
        });
      });

      this.sizeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.state.size = input.value;
          this.showError('');
          this.updateView();
          this.track('tripwire556_size_selected', { shirt_size: this.state.size });
        });
      });

      this.addButtons.forEach((button) => {
        button.addEventListener('click', (event) => this.addToCart(event));
      });

      this.setupSticky();
      this.updateView();
    }

    parseJson(selector, fallback) {
      const node = this.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent || '');
      } catch (error) {
        return fallback;
      }
    }

    getVariantKey(design, size) {
      return `${String(design || '').trim()}::${String(size || '').trim()}`;
    }

    getSelectedVariant() {
      return this.variantMap.get(this.getVariantKey(this.state.design, this.state.size));
    }

    updateView() {
      const variant = this.getSelectedVariant();
      const available = Boolean(variant?.id && variant.available !== false);
      const ctaText = this.getCtaText(variant);

      this.designButtons.forEach((button) => {
        const selected = button.dataset.design === this.state.design;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      this.sizeInputs.forEach((input) => {
        const label = input.nextElementSibling;
        if (!label) return;
        const sizeVariant = this.variantMap.get(this.getVariantKey(this.state.design, input.value));
        const isAvailable = Boolean(sizeVariant?.id && sizeVariant.available !== false);
        label.classList.toggle('is-unavailable', !isAvailable);
        input.disabled = !isAvailable;
      });

      this.classList.toggle('is-busy', this.state.busy);
      this.classList.toggle('is-unavailable', !available);

      this.setText('[data-tripwire556-selected-design]', this.state.design);
      this.setText('[data-tripwire556-selected-size]', this.state.size);
      this.setText('[data-tripwire556-price]', variant?.priceFormatted || '$69.99');
      this.setText('[data-tripwire556-cta]', this.state.busy ? 'Adding range pack...' : ctaText);
      this.setText('[data-tripwire556-sticky-cta]', this.state.busy ? 'Adding...' : (available ? 'Add Range Pack' : 'Sold Out'));

      this.addButtons.forEach((button) => {
        button.disabled = this.state.busy || !available;
        button.classList.toggle('is-loading', this.state.busy);
      });

      if (!available && !this.state.busy) {
        this.showError('This tee design and size is temporarily unavailable. Choose another size.');
      }
    }

    getCtaText(variant) {
      const price = variant?.priceFormatted || '$69.99';
      return this.primaryCta.replace('[price]', price);
    }

    setupSticky() {
      if (!this.sticky || !this.actions || !('IntersectionObserver' in window)) return;

      const finalCta = this.querySelector('[data-tripwire556-final]');
      let actionsPassed = false;
      let finalVisible = false;
      const updateSticky = () => {
        const visible = actionsPassed && !finalVisible;
        this.sticky.classList.toggle('is-visible', visible);
        this.classList.toggle('has-sticky-cta', visible);
      };

      const actionsObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          actionsPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          updateSticky();
        });
      }, { threshold: 0.1 });

      actionsObserver.observe(this.actions);

      if (finalCta) {
        const finalObserver = new IntersectionObserver((entries) => {
          finalVisible = entries.some((entry) => entry.isIntersecting);
          updateSticky();
        }, { threshold: 0.05 });

        finalObserver.observe(finalCta);
      }
    }

    async addToCart(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      if (this.state.busy) return;

      const variant = this.getSelectedVariant();
      if (!variant?.id || variant.available === false) {
        this.showError('This tee design and size is temporarily unavailable. Choose another size.');
        return;
      }

      this.setBusy(true);
      this.showError('');

      try {
        const cart = await this.fetchCart();
        const items = [this.buildBundleItem(variant)];
        const insuranceItem = this.buildInsuranceItem(cart);
        if (insuranceItem) items.push(insuranceItem);

        const addResponse = await this.nativeFetch(`${this.getRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!addResponse.ok) {
          const error = await addResponse.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Unable to add this range pack.');
        }

        await this.updateInsuranceAttribute(cart);
        window.SubscriptionInsuranceCore?.clearCartCache?.();

        this.track('tripwire556_add_to_cart', {
          shirt_design: this.state.design,
          shirt_size: this.state.size,
          bundle_variant_id: String(variant.id),
          value: variant.priceFormatted || undefined,
        });

        window.location.href = this.redirectUrl;
      } catch (error) {
        this.setBusy(false);
        this.showError(error.message || 'Unable to add this range pack. Please try again.');
      }
    }

    buildBundleItem(variant) {
      return {
        id: Number(variant.id),
        quantity: 1,
        properties: {
          _offer: '556-tripwire-shirt',
          _shirt_design: this.state.design,
          _shirt_size: this.state.size,
          _effective_ammo_cost: 'Tee and targets included',
        },
      };
    }

    buildInsuranceItem(cart) {
      if (!this.insuranceVariantId) return null;

      const hasInsurance = Array.isArray(cart?.items) && cart.items.some((item) => (
        this.insuranceVariantIds.has(Number(item.variant_id))
      ));

      if (hasInsurance) return null;

      return {
        id: this.insuranceVariantId,
        quantity: 1,
      };
    }

    async fetchCart() {
      const response = await this.nativeFetch(`${this.getRoot()}cart.js`, {
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Unable to read your cart. Please try again.');
      }

      return response.json();
    }

    async updateInsuranceAttribute(cart) {
      const attributes = { ...(cart?.attributes || {}) };
      if (attributes['shipping-insurance'] === 'true') return;

      attributes['shipping-insurance'] = 'true';

      const response = await this.nativeFetch(`${this.getRoot()}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ attributes }),
      });

      if (!response.ok) {
        throw new Error('Unable to update shipping protection. Please try again.');
      }
    }

    setBusy(isBusy) {
      this.state.busy = isBusy;
      this.updateView();
    }

    showError(message) {
      const error = this.querySelector('[data-tripwire556-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = value || '';
      });
    }

    normalizeRedirectUrl(url) {
      const redirectUrl = (url || '').trim();
      if (!redirectUrl || /^\/?cart\/?$/i.test(redirectUrl)) return '/checkout';
      return redirectUrl;
    }

    getRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    track(eventName, params = {}) {
      const payload = {
        event_category: '556_tripwire_shirt_offer',
        offer_id: '556-tripwire-shirt',
        ...params,
      };

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, ...payload });
      }

      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }
    }
  });
})();
